/**
 * Production AI chat panel surface. Composes the headless conversation engine
 * with the surface-agnostic presentational components. This replaces the old
 * AgentChat + AgentChatPanel pair.
 */

import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useConversationEngine } from '../engine/useConversationEngine';
import {
  extractReasoning,
  extractSources,
  extractText,
  extractToolActivity,
  hasToolParts,
} from '../engine/parts';
import { localStorageConversationStore } from '../persistence/localStorageConversationStore';
import { useAgentChatSettings } from '../useAgentChatSettings';
import ChatSettingsDialog from '../ChatSettingsDialog';
import MessageList from '../components/MessageList';
import MessageBubble from '../components/MessageBubble';
import StreamdownMessage from '../components/StreamdownMessage';
import ReasoningDisclosure from '../components/ReasoningDisclosure';
import StatusChip from '../components/StatusChip';
import Composer from '../components/Composer';
import DraftReviewCard from '../components/DraftReviewCard';
import Sources from '../components/Sources';
import ToolActivity from '../components/ToolActivity';
import ChatErrorBoundary from '../components/ChatErrorBoundary';
import { AuthExpiredState, EmptyState, ErrorState } from '../components/ChatStates';

import type { AgentContext } from '@/shared/types/chat';
import supabase from '@/shared/libs/supabase';

interface ChatPanelProps {
  context: AgentContext;
  onClose?: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ context, onClose }) => {
  const { settings, updateSettings, resetSettings } = useAgentChatSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const store = useMemo(() => localStorageConversationStore, []);
  const engine = useConversationEngine({ context, settings, store });

  const isStreaming = engine.status === 'streaming' || engine.status === 'submitted';
  const showEmpty =
    engine.messages.length === 0 && !engine.activeDraft && !isStreaming && !engine.error;

  // Used to avoid "dead air": if streaming but the latest assistant turn has
  // produced nothing visible yet, the footer shows a generic working chip.
  const lastMsg = engine.messages[engine.messages.length - 1];
  const lastAssistantHasContent =
    !!lastMsg &&
    lastMsg.role === 'assistant' &&
    (extractText(lastMsg).length > 0 ||
      extractReasoning(lastMsg).length > 0 ||
      hasToolParts(lastMsg));

  const draftCard = engine.activeDraft ? (
    <DraftReviewCard
      draft={engine.activeDraft}
      applying={engine.applying}
      applyError={engine.applyError}
      warnings={engine.activeDraftWarnings}
      onApply={() => {
        void engine.acceptDraft();
      }}
      onReject={engine.rejectDraft}
    />
  ) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box
        component="header"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: '1rem', fontWeight: 600, flex: 1, lineHeight: 1.4 }}
        >
          AI CoPlanner
        </Typography>
        <Tooltip title="Clear conversation">
          <span>
            <IconButton
              size="small"
              aria-label="Clear conversation"
              onClick={engine.clearConversation}
              disabled={engine.messages.length === 0 && !engine.activeDraft}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {onClose && (
          <IconButton size="small" aria-label="Close chat" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {engine.isAuthExpired ? (
        <AuthExpiredState
          onReauth={() => {
            void supabase.auth.signOut();
          }}
        />
      ) : engine.error && engine.messages.length === 0 ? (
        <ErrorState
          message={engine.error.message}
          onRetry={engine.isRateLimited ? undefined : engine.retry}
        />
      ) : showEmpty ? (
        <EmptyState />
      ) : (
        <MessageList
          messages={engine.messages}
          isStreaming={isStreaming}
          footer={
            <>
              {!engine.activeDraftMessageId && draftCard}
              {isStreaming && (engine.currentStatus || !lastAssistantHasContent) && (
                <StatusChip statuses={[{ label: engine.currentStatus ?? 'Working' }]} />
              )}
              {engine.error && (
                <ErrorState
                  message={engine.error.message}
                  onRetry={engine.isRateLimited ? undefined : engine.retry}
                />
              )}
            </>
          }
          renderItem={(message, isLast) => {
            if (message.role === 'user') {
              return <MessageBubble role="user">{extractText(message)}</MessageBubble>;
            }
            const reasoning = extractReasoning(message);
            const text = extractText(message);
            const sources = extractSources(message);
            const toolActivity = extractToolActivity(message);
            const hasDraftHere = engine.activeDraftMessageId === message.id;
            // A finished assistant turn that produced no visible text and no
            // draft must not look like a silent failure.
            const emptyFinished =
              isLast &&
              !isStreaming &&
              !text &&
              !hasDraftHere &&
              !engine.activeDraft &&
              !engine.error &&
              !hasToolParts(message);
            return (
              <ChatErrorBoundary>
                <Box>
                  {reasoning && (
                    <ReasoningDisclosure reasoningText={reasoning} streaming={isStreaming} />
                  )}
                  {isLast && isStreaming && <ToolActivity items={toolActivity} />}
                  {text && (
                    <MessageBubble role="assistant">
                      <StreamdownMessage text={text} />
                      <Sources citations={sources} />
                    </MessageBubble>
                  )}
                  {emptyFinished && (
                    <MessageBubble role="assistant">
                      <Typography variant="body2" color="text.secondary">
                        No response was generated. Please try rephrasing your request.
                      </Typography>
                    </MessageBubble>
                  )}
                  {hasDraftHere && draftCard}
                </Box>
              </ChatErrorBoundary>
            );
          }}
        />
      )}

      <Composer
        disabled={engine.isAuthExpired || engine.isRateLimited}
        isStreaming={isStreaming}
        onSend={engine.sendMessage}
        onStop={engine.stop}
        onOpenSettings={() => setSettingsOpen(true)}
        placeholder={engine.isRateLimited ? 'Rate limited — please wait…' : undefined}
      />

      {settingsOpen && (
        <ChatSettingsDialog
          open={settingsOpen}
          settings={settings}
          onClose={() => setSettingsOpen(false)}
          onSave={updateSettings}
          onReset={resetSettings}
        />
      )}
    </Box>
  );
};

export default ChatPanel;
